import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';

import RegisterForm from './RegisterForm';
import RegisterInfo from './RegisterInfo';
import StoreForm from './StoreForm';
import StoreInfo from './StoreInfo';
import ViewStores from './ViewStores';

const VIEW = {
  REGISTER_FORM: 'register-form',
  REGISTER_INFO: 'register-info',
  STORE_FORM: 'store-form',
  STORE_INFO: 'store-info',
  STORES_LIST: 'stores-list',
};

const FLASH_TIMEOUT_MS = 4000;

const FiskalyConfigTab = ({ selectedCountry }) => {
  const userToken = useSelector((state) => state.authorizationReducer.auth.accessToken);
  const [view, setView] = useState(VIEW.STORES_LIST);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [flashMessage, setFlashMessage] = useState('');
  const flashTimerRef = useRef(null);

  const clearFlashTimer = () => {
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
  };

  useEffect(() => () => clearFlashTimer(), []);

  const flash = useCallback((message) => {
    clearFlashTimer();
    setFlashMessage(message);
    flashTimerRef.current = setTimeout(() => {
      setFlashMessage('');
      flashTimerRef.current = null;
    }, FLASH_TIMEOUT_MS);
  }, []);

  const goToStoresList = () => {
    setSelectedStore(null);
    setSelectedRegister(null);
    setView(VIEW.STORES_LIST);
  };

  const goToStoreForm = () => setView(VIEW.STORE_FORM);

  const goToStoreInfo = (store) => {
    setSelectedStore(store);
    setSelectedRegister(null);
    setView(VIEW.STORE_INFO);
  };

  const goToRegisterForm = () => setView(VIEW.REGISTER_FORM);

  const goToRegisterInfo = (register) => {
    setSelectedRegister(register);
    setView(VIEW.REGISTER_INFO);
  };

  const handleStoreProvisioned = (body) => {
    flash(`Store ${body?.storeId ?? ''} provisioned successfully`);
    goToStoresList();
  };

  const handleRegisterProvisioned = (body) => {
    flash(`Register ${body?.registerId ?? ''} provisioned successfully`);
    setView(VIEW.STORE_INFO);
  };

  const handleStoreDecommissioned = (storeId) => {
    flash(`Store ${storeId} decommissioned successfully`);
    goToStoresList();
  };

  const handleRegisterDecommissioned = (registerId) => {
    flash(`Register ${registerId} decommissioned successfully`);
    setView(VIEW.STORE_INFO);
  };

  const renderView = () => {
    if (view === VIEW.STORE_FORM) {
      return (
        <StoreForm
          selectedCountry={selectedCountry}
          userToken={userToken}
          onCancel={goToStoresList}
          onProvisioned={handleStoreProvisioned}
        />
      );
    }
    if (view === VIEW.STORE_INFO && selectedStore) {
      return (
        <StoreInfo
          store={selectedStore}
          userToken={userToken}
          onBack={goToStoresList}
          onDecommissioned={handleStoreDecommissioned}
          onProvisionRegister={goToRegisterForm}
          onViewRegister={goToRegisterInfo}
        />
      );
    }
    if (view === VIEW.REGISTER_FORM && selectedStore) {
      return (
        <RegisterForm
          storeId={selectedStore.storeId}
          userToken={userToken}
          onCancel={() => setView(VIEW.STORE_INFO)}
          onProvisioned={handleRegisterProvisioned}
        />
      );
    }
    if (view === VIEW.REGISTER_INFO && selectedRegister) {
      return (
        <RegisterInfo
          register={selectedRegister}
          userToken={userToken}
          onBack={() => setView(VIEW.STORE_INFO)}
          onDecommissioned={handleRegisterDecommissioned}
        />
      );
    }
    return (
      <ViewStores
        userToken={userToken}
        onProvisionStore={goToStoreForm}
        onViewStore={goToStoreInfo}
      />
    );
  };

  return (
    <section className="soa-fiskaly-tab">
      {flashMessage && (
        <div
          aria-live="polite"
          className="text-color-success body-2 ta-sm-c mb2-sm"
          role="status"
        >
          {flashMessage}
        </div>
      )}
      {renderView()}
    </section>
  );
};

FiskalyConfigTab.defaultProps = {
  selectedCountry: null,
};

FiskalyConfigTab.propTypes = {
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

export default FiskalyConfigTab;
